/**
 * useBrowseResumeRefresh — 앱이 백그라운드에서 일정 시간 이상 머문 뒤 포그라운드로
 * 복귀하면 browse 성격의 목록 데이터를 최신화한다.
 *
 * 배경:
 *   메인 탭은 keep-alive(unmount 안 함, display:none 토글)라 탭을 떠났다 와도 재마운트가
 *   없고, 전역 refetchOnWindowFocus=false 라 staleTime 만료만으로는 재패칭 트리거가 없다.
 *   → 오래 이탈 후 복귀 시 데이터가 낡은 채로 남는 공백을 메운다.
 *
 * 정책:
 *   - minBackgroundMs(120초) 이상 백그라운드 체류했을 때만 발사 → 알림 잠깐 보고 복귀
 *     하는 경우의 불필요한 refetch/깜빡임 방지. (게이트는 useAppResume 이 담당.)
 *   - invalidateQueries(refetchType 기본 'active') → 현재 관찰 중인 쿼리만 즉시
 *     백그라운드 refetch. data 는 유지되므로 화면 깜빡임 없음.
 *
 * 템플릿: 기본 대상은 알림 인박스뿐. 프로젝트에서 홈/목록 query key 를 추가.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAppResume } from "./useAppResume";
import { NOTIFICATIONS_KEY } from "./useNotificationQueries";

/** 복귀 시 최신화 대상 query key (prefix 매칭). 프로젝트에서 추가. */
const BROWSE_REFRESH_KEYS: readonly (readonly unknown[])[] = [
  NOTIFICATIONS_KEY, // 알림 인박스 (AppBar 뱃지 포함)
];

/** 이 시간(ms) 미만 백그라운드 체류는 무시 — 잠깐 이탈 후 복귀 시 깜빡임 방지. */
const BROWSE_RESUME_MIN_BACKGROUND_MS = 120_000; // 2분

export function useBrowseResumeRefresh(): void {
  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    for (const queryKey of BROWSE_REFRESH_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient]);

  useAppResume(refresh, { minBackgroundMs: BROWSE_RESUME_MIN_BACKGROUND_MS });
}
